import { type ReactNode, type RefObject, useEffect, useRef } from 'react';
import { useBodyScrollLock } from '@/lib/scroll-lock';
import { cn } from '@/lib/utils';

/**
 * The one way the console opens something over the page (issue #89, building on
 * #62).
 *
 * Before this there were three near-copies of "float something over the rest of
 * the page": the board's detail pane (`SplitPane`'s `overlay`, issue #62) grew
 * its own scrim, Escape handling and focus return; the command palette has
 * another copy of the same three; and a verb popover or a job composer opened
 * over a page would have been a third and fourth if each wrote its own. The
 * pieces here - Escape-to-close, a click outside that closes, focus that
 * returns to whatever opened it, and the scrim itself - are what `SplitPane`
 * now shares with the verb popovers and the job composer this issue adds. None
 * of them render a shape by themselves; each caller still decides whether it is
 * a corner popover, a centred modal or a side panel; what they can no longer
 * disagree on is how dismissal and focus behave.
 */

/**
 * Escape closes, unless something else already owns the key.
 *
 * A window listener rather than one on the panel itself, because the panel is
 * not always where focus is (a trigger button, a nested field). `suppressed`
 * is for a caller with something else modal already open over it - the palette
 * or the phone nav sheet - so the two Escapes cannot both fire on one press.
 */
export function useEscapeToClose(active: boolean, onClose: () => void, suppressed = false): void {
  useEffect(() => {
    if (!active || suppressed) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, suppressed, onClose]);
}

/**
 * A pointerdown outside `ref`'s node closes it, the same gesture `AskBox` and
 * `Menu` already use for a question or a list nobody meant to leave open.
 */
export function useOutsideDismiss(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  onClose: () => void,
): void {
  useEffect(() => {
    if (!active) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [ref, active, onClose]);
}

/**
 * Focus moves onto `focusRef` while `active` is true (when one is given), and
 * back to whatever was focused before, once it isn't - as long as that element
 * is still on the page. A list that re-sorted while a panel was open may not
 * have it any more, and focusing a detached node moves focus to nowhere.
 */
export function useReturnFocus(active: boolean, focusRef?: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement as HTMLElement | null;
    focusRef?.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [active, focusRef]);
}

/** The scrim behind a modal: a full-screen button, so a click anywhere on it closes. */
export function Scrim({
  onClose,
  label = 'Close',
  className,
}: {
  onClose: () => void;
  label?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClose}
      className={cn('dy-scrim-in absolute inset-0', className)}
      style={{ background: 'var(--dy-overlay)' }}
    />
  );
}

/**
 * A dialog over the whole page: a scrim, a centred card, Escape and the scrim
 * both closing it, and the body behind it locked so a long form does not carry
 * the page's own scroll along with it. Drawn from the command palette's own
 * shape (`bg-pop`/`dy-pop-in`/`shadow-pop`, the same well every floating panel
 * in the console already opens from), so a job composer over the job page
 * reads as the same kind of thing the palette already is rather than a fourth
 * one. The job composer (issue #89) is the first caller that is not the
 * palette itself; a future one reaches for this rather than writing a fifth
 * scrim.
 *
 * Nothing here decides what is inside: the caller's own first field still
 * takes its own focus on mount, the way the composer already did before this
 * had a name. This only guarantees the dialog closes cleanly and hands focus
 * back to whatever opened it.
 */
export function Modal({
  open,
  onClose,
  label,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useEscapeToClose(open, onClose);
  useBodyScrollLock(open);
  // No `focusRef`: the dialog's own content (the composer's title field, so
  // far) takes its own focus on mount, the way it already did before this had
  // a name. This only has to put focus back where it was once the dialog
  // closes, not fight the child for it on the way in.
  useReturnFocus(open);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-3 sm:p-4">
      <Scrim onClose={onClose} label={`Close ${label}`} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={cn(
          'bg-pop dy-pop-in relative mt-[8vh] w-full max-w-xl overflow-hidden rounded-xl border shadow-pop outline-none',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
